/**
 * LanguageSwitcher Component
 *
 * Provides a dropdown menu to switch between English, Simplified Chinese, and Traditional Chinese.
 * Displays current language and allows seamless switching.
 */

import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "react-i18next";

const LanguageSwitcher = ({ variant = "desktop" }) => {
  const { currentLanguage, selectLanguage, getLanguageDisplayName } =
    useLanguage();
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Language options
  const languages = [
    { code: "en", label: "EN", fullName: "English" },
    { code: "zh-CN", label: "简中", fullName: "简体中文" },
    { code: "zh-TW", label: "繁中", fullName: "繁體中文" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageSelect = (langCode) => {
    selectLanguage(langCode);
    setIsOpen(false);
  };

  // Desktop style - compact dropdown with themed globe icon
  if (variant === "desktop") {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-800/50 hover:bg-gray-700/50 border border-gray-600/50 hover:border-violet-400/50 text-gray-300 hover:text-white transition-all duration-300 hover:scale-105"
          aria-label={t("language.switch")}
          title={t("language.switch")}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
            <defs>
              <linearGradient
                id="globeGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
            </defs>
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="url(#globeGradient)"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
              stroke="url(#globeGradient)"
              strokeWidth="2"
              fill="none"
            />
          </svg>
          <span className="text-sm font-medium">
            {getLanguageDisplayName()}
          </span>
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-40 rounded-xl bg-gray-800/95 backdrop-blur-sm border border-gray-600/50 shadow-xl overflow-hidden z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                className={`w-full px-4 py-3 text-left text-sm transition-colors duration-200 flex items-center justify-between ${
                  currentLanguage === lang.code
                    ? "bg-violet-600/20 text-violet-400 font-medium"
                    : "text-gray-300 hover:bg-gray-700/50 hover:text-white"
                }`}
              >
                <span>{lang.fullName}</span>
                <span className="text-xs">{lang.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Mobile header style - compact dropdown for header bar
  if (variant === "mobile-header") {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 border border-gray-600/50 hover:border-violet-400/50 text-gray-300 hover:text-white transition-all duration-300"
          aria-label={t("language.switch")}
          title={t("language.switch")}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
            <defs>
              <linearGradient
                id="globeGradientMobileHeader"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
            </defs>
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="url(#globeGradientMobileHeader)"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
              stroke="url(#globeGradientMobileHeader)"
              strokeWidth="2"
              fill="none"
            />
          </svg>
          <span className="text-xs font-medium">
            {getLanguageDisplayName()}
          </span>
          <svg
            className={`w-3 h-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-36 rounded-lg bg-gray-800/95 backdrop-blur-sm border border-gray-600/50 shadow-xl overflow-hidden z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                className={`w-full px-3 py-2 text-left text-xs transition-colors duration-200 flex items-center justify-between ${
                  currentLanguage === lang.code
                    ? "bg-violet-600/20 text-violet-400 font-medium"
                    : "text-gray-300 hover:bg-gray-700/50 hover:text-white"
                }`}
              >
                <span>{lang.fullName}</span>
                <span className="text-xs">{lang.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Mobile style - full width dropdown with themed globe icon
  if (variant === "mobile") {
    return (
      <div className="w-full" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-700/30 transition-colors duration-300 rounded-lg"
          aria-label={t("language.switch")}
        >
          <span className="flex items-center gap-3">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <defs>
                <linearGradient
                  id="globeGradientMobile"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#a855f7" />
                </linearGradient>
              </defs>
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="url(#globeGradientMobile)"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                stroke="url(#globeGradientMobile)"
                strokeWidth="2"
                fill="none"
              />
            </svg>
            <span className="font-medium">{t("language.switch")}</span>
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">
              {getLanguageDisplayName()}
            </span>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </button>

        {isOpen && (
          <div className="mt-2 w-full rounded-lg bg-gray-800/50 border border-gray-600/30 overflow-hidden">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                className={`w-full px-4 py-3 text-left transition-colors duration-200 flex items-center justify-between ${
                  currentLanguage === lang.code
                    ? "bg-violet-600/20 text-violet-400 font-medium"
                    : "text-gray-300 hover:bg-gray-700/30 hover:text-white"
                }`}
              >
                <span>{lang.fullName}</span>
                <span className="text-sm">{lang.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default LanguageSwitcher;
